import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Col, Row, Typography, Tooltip, Button, Tag, message } from 'antd';
import { InboxOutlined, EyeOutlined } from '@ant-design/icons';
import type { RouteViewModelCargoSummary } from '../../../types/routeViewModel';
import { CargoStatus, CARGO_STATUS_COLORS, CARGO_STATUS_LABELS } from '../../../types';
import { DataTable } from '../../../ui';
import { useGetCargoByIdQuery } from '../../../store/api/cargoApi';
import { routesApi } from '../../../store/api/routesApi';
import { fetchCargoAllocationIndexForApplications } from '../../../utils/fetchCargoAllocationIndexForApplications';
import type { AppDispatch } from '../../../store';
import { transportStatusTagColor } from '../../../utils/cargoDerivedTransportStatus';
import { isCargoLifecycleProjectionUiEnabled } from '../../../services/cargoLifecycle/cargoLifecycleProjectionFlags';
import { useCargoLifecycleProjectionIndex } from '../../../hooks/useCargoLifecycleProjectionIndex';
import { resolveCargoCounterSnapshot } from '../../../utils/cargoCounterSnapshot';
import CargoDetailsModal from '../../cargo/CargoDetailsModal';
import { navigateToReferenceCard } from '../../../utils/referenceCardNavigation';
const { Text } = Typography;

/** Подписи для карточки маршрута (кратко) */
const STATUS_IN_ROUTE_VIEW: Partial<Record<CargoStatus, string>> = {
  [CargoStatus.IN_ROUTE]: 'В пути',
  [CargoStatus.ON_WAREHOUSE]: 'На складе',
  [CargoStatus.DELIVERED]: 'Доставлен',
  [CargoStatus.IN_APPLICATION]: 'В заявке'
};

export interface CargoBlockProps {
  cargoLines: Record<string, unknown>[];
  /** Итоги по грузам маршрута (как в структуре маршрута) */
  summary: RouteViewModelCargoSummary;
  /** Аллокации маршрута (для колонки «взято / всего») */
  allocations?: Record<string, unknown>[];
  /** Текущий маршрут — для active qty на карточке маршрута. */
  routeId?: number;
  /** Заявки маршрута — для сводки распределения груза по всем маршрутам заявки в модалке (как на карточке заявки). */
  linkedApplicationIds?: number[];
  /** Меняется при refetch карточки маршрута (view), чтобы пересобрать индекс аллокаций. */
  allocationRefreshToken?: string;
}

function numOrDash(v: unknown, fractionDigits: number): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: fractionDigits });
}

function parseCargoStatus(raw: unknown): CargoStatus | null {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  const found = (Object.values(CargoStatus) as string[]).find((v) => v === s);
  return found ? (found as CargoStatus) : null;
}

function statusTagLabel(st: CargoStatus | null): string {
  if (!st) return '—';
  return STATUS_IN_ROUTE_VIEW[st] ?? CARGO_STATUS_LABELS[st] ?? '—';
}

const CargoBlock: React.FC<CargoBlockProps> = ({
  cargoLines,
  summary,
  allocations,
  routeId,
  linkedApplicationIds,
  allocationRefreshToken = '',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const projectionUiEnabled = isCargoLifecycleProjectionUiEnabled();
  const [openCargoId, setOpenCargoId] = useState<number | null>(null);
  const [crossRouteAllocationIndex, setCrossRouteAllocationIndex] = useState(
    () =>
      new Map<number, { inRoutesTotal: number; byRoute: { routeId: number; amount: number }[] }>()
  );
  const [crossRouteAllocationLoading, setCrossRouteAllocationLoading] = useState(false);
  const [routeStatusById, setRouteStatusById] = useState(() => new Map<number, string>());

  const cargoIdsOnRoute = useMemo(() => {
    const ids = new Set<number>();
    for (const row of cargoLines || []) {
      const inner = (row.cargo as Record<string, unknown> | undefined) ?? row;
      const cid = inner?.id != null && Number.isFinite(Number(inner.id)) ? Number(inner.id) : null;
      if (cid != null) ids.add(cid);
    }
    return Array.from(ids);
  }, [cargoLines]);

  const {
    index: projectionIndex,
    loading: projectionIndexLoading,
    failed: projectionIndexFailed,
  } = useCargoLifecycleProjectionIndex(cargoIdsOnRoute, {
    enabled: projectionUiEnabled,
    refreshKey: `${allocationRefreshToken}|${cargoIdsOnRoute.join(',')}`,
  });

  const linkedApplicationIdsKey = useMemo(
    () =>
      (linkedApplicationIds ?? [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)
        .join(','),
    [linkedApplicationIds]
  );

  useEffect(() => {
    if (projectionUiEnabled) {
      setCrossRouteAllocationIndex(new Map());
      setCrossRouteAllocationLoading(false);
      return;
    }
    if (!linkedApplicationIdsKey) {
      setCrossRouteAllocationIndex(new Map());
      setCrossRouteAllocationLoading(false);
      return;
    }
    const appIds = linkedApplicationIdsKey.split(',').map(Number).filter((n) => n > 0);
    let cancelled = false;
    setCrossRouteAllocationLoading(true);
    (async () => {
      try {
        const index = await fetchCargoAllocationIndexForApplications(dispatch, appIds);
        if (!cancelled) {
          setCrossRouteAllocationIndex(index);
        }
      } catch (e) {
        console.error('[CargoBlock] cross-route allocations failed', { linkedApplicationIdsKey, e });
        message.warning('Не удалось загрузить распределение груза по маршрутам заявки');
      } finally {
        if (!cancelled) {
          setCrossRouteAllocationLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkedApplicationIdsKey, allocationRefreshToken, dispatch, projectionUiEnabled]);

  const crossRouteRouteIdsKey = useMemo(() => {
    const ids = new Set<number>();
    if (projectionUiEnabled) {
      for (const p of Array.from(projectionIndex.values())) {
        for (const r of p.byRoute) {
          if (Number.isFinite(r.routeId) && r.routeId > 0) ids.add(r.routeId);
        }
      }
    } else {
      for (const v of Array.from(crossRouteAllocationIndex.values())) {
        for (const r of v.byRoute) {
          if (Number.isFinite(r.routeId) && r.routeId > 0) ids.add(r.routeId);
        }
      }
    }
    if (routeId != null && Number.isFinite(routeId)) ids.add(routeId);
    return Array.from(ids)
      .sort((a, b) => a - b)
      .join(',');
  }, [crossRouteAllocationIndex, projectionIndex, projectionUiEnabled, routeId]);

  const crossRouteAllocFingerprint = useMemo(() => {
    if (projectionUiEnabled) {
      if (!projectionIndex.size) return '';
      return Array.from(projectionIndex.entries())
        .sort((a, b) => a[0] - b[0])
        .map(
          ([cid, p]) =>
            `${cid}:${p.inRoutesActive}:${p.byRoute.map((x) => `${x.routeId}:${x.active}`).sort().join(',')}`
        )
        .join('|');
    }
    if (!crossRouteAllocationIndex.size) return '';
    return Array.from(crossRouteAllocationIndex.entries())
      .sort((a, b) => a[0] - b[0])
      .map(
        ([cid, v]) =>
          `${cid}:${v.inRoutesTotal}:${v.byRoute.map((x) => `${x.routeId}:${x.amount}`).sort().join(',')}`
      )
      .join('|');
  }, [crossRouteAllocationIndex, projectionIndex, projectionUiEnabled]);

  const routeStatusesFingerprint = useMemo(() => {
    if (!crossRouteRouteIdsKey) return '';
    return crossRouteRouteIdsKey
      .split(',')
      .map((idStr) => {
        const id = Number(idStr);
        return `${id}:${routeStatusById.get(id) ?? ''}`;
      })
      .join(',');
  }, [crossRouteRouteIdsKey, routeStatusById]);

  const cargoModalHistoryKey = useMemo(
    () =>
      `${linkedApplicationIdsKey}|${allocationRefreshToken}|${crossRouteAllocFingerprint}|${routeStatusesFingerprint}`,
    [linkedApplicationIdsKey, allocationRefreshToken, crossRouteAllocFingerprint, routeStatusesFingerprint]
  );

  useEffect(() => {
    if (!crossRouteRouteIdsKey) {
      setRouteStatusById(new Map());
      return;
    }
    const ids = crossRouteRouteIdsKey.split(',').map(Number).filter((n) => n > 0);
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        ids.map(async (id): Promise<[number, string] | null> => {
          try {
            const r = await dispatch(
              routesApi.endpoints.getRouteById.initiate(id, { forceRefetch: true })
            ).unwrap();
            const st = r?.status != null ? String(r.status) : '';
            return [id, st];
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      const m = new Map<number, string>();
      for (const row of results) {
        if (row && row[1] !== '') m.set(row[0], row[1]);
      }
      setRouteStatusById(m);
    })();
    return () => {
      cancelled = true;
    };
  }, [crossRouteRouteIdsKey, allocationRefreshToken, dispatch]);

  const allocatedByCargoId = useMemo(() => {
    const m = new Map<number, number>();
    for (const a of allocations || []) {
      const cid = Number((a as { cargoId?: unknown }).cargoId);
      if (!Number.isFinite(cid)) continue;
      const q = Number((a as { allocatedQuantity?: unknown }).allocatedQuantity ?? 0);
      if (!Number.isFinite(q)) continue;
      m.set(cid, (m.get(cid) || 0) + q);
    }
    return m;
  }, [allocations]);

  const {
    data: cargoDetails,
    isFetching,
    isLoading,
    isError
  } = useGetCargoByIdQuery(openCargoId!, {
    skip: openCargoId === null
  });

  const detailsLoading =
    openCargoId !== null && !cargoDetails && (isLoading || isFetching);

  useEffect(() => {
    if (isError && openCargoId != null) {
      message.error('Не удалось загрузить груз');
      setOpenCargoId(null);
    }
  }, [isError, openCargoId]);

  const countersLoading = projectionUiEnabled ? projectionIndexLoading : crossRouteAllocationLoading;

  const cargoModalCounterSnapshot = useMemo(() => {
    if (openCargoId == null || !cargoDetails?.id) return null;
    return resolveCargoCounterSnapshot({
      cargoId: cargoDetails.id,
      quantity: Number(cargoDetails.quantity ?? 0),
      projectionIndex,
      legacyIndex: crossRouteAllocationIndex,
      routeStatusById,
      useProjection: projectionUiEnabled,
    });
  }, [
    openCargoId,
    cargoDetails,
    projectionIndex,
    crossRouteAllocationIndex,
    routeStatusById,
    projectionUiEnabled,
  ]);

  const handleOpenCargo = (id: number | null) => {
    if (id == null || !Number.isFinite(Number(id))) return;
    setOpenCargoId(Number(id));
  };

  const rows = useMemo(() => {
    const list = cargoLines || [];
    return list.map((row, idx) => {
      const inner = (row.cargo as Record<string, unknown> | undefined) ?? row;
      const cargoId =
        inner?.id != null && Number.isFinite(Number(inner.id)) ? Number(inner.id) : null;
      const name =
        inner?.name != null && String(inner.name).trim()
          ? String(inner.name).trim()
          : inner?.description != null && String(inner.description).trim()
            ? String(inner.description).trim()
            : '—';
      const descRaw =
        inner?.description != null && String(inner.description).trim()
          ? String(inner.description).trim()
          : '—';
      const packaging =
        inner?.packagingType != null && String(inner.packagingType).trim()
          ? String(inner.packagingType).trim()
          : '—';
      const totalQtyRaw = Number(inner?.quantity ?? 0);
      const totalQty = Number.isFinite(totalQtyRaw) ? totalQtyRaw : 0;

      let takenVsTotal = '—';
      if (cargoId != null && totalQty > 0) {
        if (projectionUiEnabled) {
          const projection = projectionIndex.get(cargoId);
          const routeRow =
            routeId != null
              ? projection?.byRoute.find((r) => r.routeId === routeId)
              : projection?.byRoute[0];
          const activeOnRoute = routeRow?.active ?? 0;
          takenVsTotal = `${activeOnRoute} / ${totalQty}`;
        } else {
          const allocated = allocatedByCargoId.get(cargoId);
          takenVsTotal =
            allocated !== undefined
              ? `${allocated} / ${totalQty}`
              : `— / ${totalQty}`;
        }
      }

      const app = inner?.application as Record<string, unknown> | undefined;
      const applicationId =
        app?.id != null && Number.isFinite(Number(app.id)) ? Number(app.id) : null;
      const appNumber =
        app?.internalNumber != null && String(app.internalNumber).trim()
          ? String(app.internalNumber).trim()
          : applicationId != null
            ? `#${applicationId}`
            : '—';
      const qty = numOrDash(inner?.quantity, 0);
      const weight = numOrDash(inner?.totalWeightKg, 2);
      const vol = numOrDash(inner?.volumeM3, 3);
      const st = parseCargoStatus(inner?.status);

      let transportLabel: string | null = null;
      if (cargoId != null) {
        const snap = resolveCargoCounterSnapshot({
          cargoId,
          quantity: totalQty,
          projectionIndex,
          legacyIndex: crossRouteAllocationIndex,
          routeStatusById,
          useProjection: projectionUiEnabled,
        });
        transportLabel = linkedApplicationIdsKey || projectionUiEnabled ? snap.transportLabel : null;
      }

      const tooltipTitle = (
        <div style={{ maxWidth: 320, lineHeight: 1.5 }}>
          <div>
            <Text type="secondary">Описание: </Text>
            {descRaw}
          </div>
          <div>
            <Text type="secondary">Вес: </Text>
            {weight === '—' ? '—' : `${weight} кг`}
          </div>
          <div>
            <Text type="secondary">Объём: </Text>
            {vol === '—' ? '—' : `${vol} м³`}
          </div>
        </div>
      );
      return {
        key: cargoId != null ? String(cargoId) : `r-${idx}`,
        cargoId,
        applicationId,
        appNumber,
        takenVsTotal,
        name,
        tooltipTitle,
        packaging,
        qty,
        weight,
        volume: vol,
        status: st,
        transportLabel,
      };
    });
  }, [
    cargoLines,
    allocatedByCargoId,
    crossRouteAllocationIndex,
    linkedApplicationIdsKey,
    routeStatusById,
    projectionIndex,
    projectionUiEnabled,
    routeId,
  ]);

  const cs = summary;

  return (
    <Card
      size="small"
      title={
        <span>
          <InboxOutlined style={{ marginRight: 8 }} />
          Грузы
        </span>
      }
      style={{ marginBottom: 16 }}
    >
      <CargoDetailsModal
        cargo={cargoDetails ?? null}
        visible={openCargoId !== null}
        loading={detailsLoading}
        onClose={() => setOpenCargoId(null)}
        counterSnapshot={cargoModalCounterSnapshot}
        counterSnapshotLoading={countersLoading && openCargoId !== null}
        projectionFailed={projectionUiEnabled && projectionIndexFailed}
        historyRefreshKey={cargoModalHistoryKey}
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 'bold' }}>{cs.totalQuantityPlaces}</div>
            <div style={{ fontSize: 12, color: '#666' }}>Мест</div>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 'bold' }}>
              {cs.totalWeightKg.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>Вес, кг</div>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 'bold' }}>
              {cs.totalVolumeM3.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>Объём, м³</div>
          </Card>
        </Col>
      </Row>

      {rows.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13 }}>—</Text>
      ) : (
        <DataTable
          size="small"
          pagination={false}
          rowKey="key"
          dataSource={rows}
          rowClassName={(record) =>
            record.cargoId != null ? 'route-details-cargo-row' : ''
          }
          onRow={(record) => ({
            onClick: () => handleOpenCargo(record.cargoId),
            style:
              record.cargoId != null
                ? { cursor: 'pointer' }
                : { cursor: 'default' }
          })}
          columns={[
            {
              title: 'Заявка',
              dataIndex: 'appNumber',
              width: 120,
              ellipsis: true,
                render: (text: string, record: (typeof rows)[0]) =>
                record.applicationId != null ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToReferenceCard(navigate, `/applications/${record.applicationId!}`, location);
                    }}
                  >
                    {text}
                  </Button>
                ) : (
                  text
                )
            },
            {
              title: 'Наименование',
              dataIndex: 'name',
              ellipsis: true,
              render: (name: string, record: (typeof rows)[0]) => (
                <Tooltip title={record.tooltipTitle} mouseEnterDelay={0.3}>
                  <span>{name}</span>
                </Tooltip>
              )
            },
            {
              title: projectionUiEnabled ? 'В маршруте (актив / всего)' : 'В маршруте (взято / всего)',
              dataIndex: 'takenVsTotal',
              width: 180,
              align: 'right' as const
            },
            {
              title: 'Перевозка',
              dataIndex: 'transportLabel',
              width: 150,
              render: (tl: string | null, record: (typeof rows)[0]) =>
                tl != null ? (
                  <Tag color={transportStatusTagColor(tl)}>{tl}</Tag>
                ) : record.status ? (
                  <Tag color={CARGO_STATUS_COLORS[record.status]}>{statusTagLabel(record.status)}</Tag>
                ) : (
                  '—'
                )
            },
            {
              title: 'Статус (БД)',
              dataIndex: 'status',
              width: 110,
              render: (st: CargoStatus | null) =>
                st ? (
                  <Tag color={CARGO_STATUS_COLORS[st]}>{statusTagLabel(st)}</Tag>
                ) : (
                  '—'
                )
            },
            { title: 'Упаковка', dataIndex: 'packaging', width: 120, ellipsis: true },
            { title: 'Кол-во мест', dataIndex: 'qty', width: 100, align: 'right' as const },
            {
              title: 'Вес, кг',
              dataIndex: 'weight',
              width: 90,
              align: 'right' as const,
              render: (w: string) => (w === '—' ? '—' : w)
            },
            {
              title: 'Объём, м³',
              dataIndex: 'volume',
              width: 90,
              align: 'right' as const,
              render: (v: string) => (v === '—' ? '—' : v)
            },
            {
              title: '',
              key: 'open',
              width: 44,
              align: 'center' as const,
              render: (_: unknown, record: (typeof rows)[0]) =>
                record.cargoId != null ? (
                  <Button
                    type="text"
                    size="small"
                    title="Открыть"
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenCargo(record.cargoId);
                    }}
                  />
                ) : null
            }
          ]}
        />
      )}
      <style>{`
        .route-details-cargo-row.ant-table-row:hover > td {
          background: rgba(0, 0, 0, 0.04) !important;
        }
      `}</style>
    </Card>
  );
};

export default CargoBlock;
